(() => {
  const SELECTORS = {
    root: '[data-oneshot-pdp]',
    variants: '[data-product-variants]',
    sizeButton: '[data-size-value]',
    colorButton: '[data-color-value]',
    mainButton: '.oneshot-pdp__add',
    mainLabel: '[data-add-label]',
    stickyButton: '[data-sticky-add]',
    stickyLabel: '[data-sticky-label]',
  };

  const parseVariants = (root) => {
    const source = root.querySelector(SELECTORS.variants);

    if (!source) return [];

    try {
      const variants = JSON.parse(source.textContent);
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      console.warn('OneShot PDP: dati variante non leggibili.', error);
      return [];
    }
  };

  const initGallery = (root) => {
    const gallery = root.querySelector('[data-oneshot-gallery]');
    if (!gallery) return;

    const slides = [...gallery.querySelectorAll('slideshow-slide')];
    const thumbnailGroups = [...gallery.querySelectorAll('.slideshow-controls__thumbnails')];
    const counter = gallery.querySelector('[data-gallery-counter]');
    const zoomButton = gallery.querySelector('[data-gallery-zoom]');
    const stage = gallery.querySelector('slideshow-container');
    const mobileGallery = window.matchMedia('(max-width: 899px)');

    if (!slides.length || !stage) return;

    slides.forEach((slide, index) => {
      const image = slide.querySelector('.product-media__image');
      if (!image) return;

      if (index === 0) {
        image.loading = 'eager';
        image.setAttribute('fetchpriority', 'high');
      } else {
        image.loading = 'lazy';
        image.setAttribute('loading', 'lazy');
      }
    });

    const currentIndex = () => {
      const activeSlide = slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false');
      if (activeSlide >= 0) return activeSlide;

      const firstGroup = thumbnailGroups[0];
      if (!firstGroup) return 0;

      const buttons = [...firstGroup.querySelectorAll('.slideshow-controls__thumbnail')];
      const activeThumbnail = buttons.findIndex((button) => button.getAttribute('aria-selected') === 'true');
      return activeThumbnail >= 0 ? activeThumbnail : 0;
    };

    const syncGalleryState = () => {
      const index = currentIndex();
      if (counter) counter.textContent = `${index + 1} / ${slides.length}`;

      thumbnailGroups.forEach((group) => {
        [...group.querySelectorAll('.slideshow-controls__thumbnail')].forEach((button, buttonIndex) => {
          button.setAttribute('aria-pressed', String(buttonIndex === index));
        });
      });
    };

    const selectSlide = (index) => {
      const normalizedIndex = (index + slides.length) % slides.length;
      const visibleGroup = thumbnailGroups.find((group) => group.getClientRects().length > 0) || thumbnailGroups[0];
      const button = visibleGroup?.querySelectorAll('.slideshow-controls__thumbnail')[normalizedIndex];

      if (button) {
        button.click();
        button.scrollIntoView({ block: 'nearest', inline: 'center' });
      }
    };

    const galleryObserver = new MutationObserver(syncGalleryState);
    galleryObserver.observe(gallery, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'aria-selected'],
    });

    zoomButton?.addEventListener('click', () => {
      slides[currentIndex()]?.click();
    });

    let touchStartX = null;
    let touchStartIndex = 0;

    stage.addEventListener(
      'touchstart',
      (event) => {
        if (!mobileGallery.matches) return;
        touchStartX = event.touches[0]?.clientX ?? null;
        touchStartIndex = currentIndex();
      },
      { passive: true }
    );

    stage.addEventListener(
      'touchend',
      (event) => {
        if (!mobileGallery.matches) {
          touchStartX = null;
          return;
        }
        if (touchStartX === null) return;

        const touchEndX = event.changedTouches[0]?.clientX ?? touchStartX;
        const deltaX = touchEndX - touchStartX;
        touchStartX = null;

        if (Math.abs(deltaX) < 40) return;
        selectSlide(touchStartIndex + (deltaX < 0 ? 1 : -1));
      },
      { passive: true }
    );

    syncGalleryState();
  };

  const initProduct = (root) => {
    if (root.dataset.oneshotInitialized === 'true') return;
    root.dataset.oneshotInitialized = 'true';

    const variants = parseVariants(root);
    const threshold = Number.parseInt(root.dataset.lowStockThreshold, 10) || 3;
    const readyTime = root.dataset.readyTime || '48–72 ore';
    const preorderTime = root.dataset.preorderTime || '35–40 giorni';
    // I testi visibili arrivano tradotti dal Liquid: qui non si scrive italiano.
    const t = {
      scegliTaglia: root.dataset.tScegliTaglia || '',
      preordina: root.dataset.tPreordina || '',
      aggiungi: root.dataset.tAggiungi || '',
      suOrdinazione: root.dataset.tSuOrdinazione || '',
      inMagazzino: root.dataset.tInMagazzino || '',
      neRestanoUno: root.dataset.tNeRestanoUno || '',
      neRestano: root.dataset.tNeRestano || '',
      consegnaIn: root.dataset.tConsegnaIn || '',
      parteIn: root.dataset.tParteIn || '',
      propPreordine: root.dataset.tPropPreordine || '',
      propPronta: root.dataset.tPropPronta || '',
    };
    const colorPosition = Number.parseInt(root.dataset.colorPosition, 10) || 0;

    const sizeButtons = [...root.querySelectorAll(SELECTORS.sizeButton)];
    const colorButtons = [...root.querySelectorAll(SELECTORS.colorButton)];
    const colorLabel = root.querySelector('[data-color-label]');
    const status = root.querySelector('[data-variant-status]');
    const statusTitle = root.querySelector('[data-status-title]');
    const statusDetail = root.querySelector('[data-status-detail]');
    const price = root.querySelector('[data-product-price]');
    const stickyPrice = root.querySelector('[data-sticky-price]');
    const stickyVariant = root.querySelector('[data-sticky-variant]');
    const stickyImage = root.querySelector('[data-sticky-image]');
    const variantInput = root.querySelector('[data-variant-input]');
    const propertyInput = root.querySelector('[data-availability-property]');
    const quantityInput = root.querySelector('[data-quantity-input]');
    const quantityMinus = root.querySelector('[data-quantity-minus]');
    const quantityPlus = root.querySelector('[data-quantity-plus]');
    const mainButton = root.querySelector(SELECTORS.mainButton);
    const mainLabel = root.querySelector(SELECTORS.mainLabel);
    const shippingNote = root.querySelector('[data-shipping-note]');
    const acceleratedCheckout = root.querySelector('[data-accelerated-checkout]');
    const stickyBar = root.querySelector('[data-sticky-bar]');
    const stickyButton = root.querySelector(SELECTORS.stickyButton);
    const stickyLabel = root.querySelector(SELECTORS.stickyLabel);
    const productForm = mainButton?.form;

    let selectedVariant = null;
    let selectedSize = '';
    let selectedColor =
      colorButtons.find((button) => button.getAttribute('aria-checked') === 'true')?.dataset.colorValue ||
      colorLabel?.textContent.trim() ||
      '';

    const inventoryState = (variant) => {
      const inventory = Number(variant?.inventory) || 0;
      if (inventory <= 0) return 'preorder';
      if (inventory <= threshold) return 'low';
      return 'stock';
    };

    const quantityMaximum = (variant) => {
      if (!variant || inventoryState(variant) === 'preorder') return 10;
      return Math.max(1, Number(variant.inventory) || 1);
    };

    const syncQuantity = () => {
      if (!quantityInput) return;

      const maximum = quantityMaximum(selectedVariant);
      const current = Math.min(maximum, Math.max(1, Number.parseInt(quantityInput.value, 10) || 1));
      quantityInput.value = String(current);
      quantityInput.max = String(maximum);
      quantityInput.setAttribute('aria-valuemax', String(maximum));

      if (quantityMinus) quantityMinus.disabled = current <= 1;
      if (quantityPlus) quantityPlus.disabled = current >= maximum;
    };

    const setButtonState = (label, disabled) => {
      if (mainLabel) mainLabel.textContent = label;
      if (mainButton) mainButton.disabled = disabled;
      if (stickyLabel) stickyLabel.textContent = label;
      if (stickyButton) stickyButton.disabled = disabled;
    };

    const syncStickyFromMain = () => {
      if (!mainButton || !stickyButton) return;
      stickyButton.disabled = mainButton.disabled;
      if (stickyLabel && mainLabel) stickyLabel.textContent = mainLabel.textContent;
    };

    const updateUrl = (variant) => {
      if (!variant || !window.history?.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url.toString());
    };

    const announceVariantInput = () => {
      if (!variantInput) return;
      variantInput.dispatchEvent(new Event('input', { bubbles: true }));
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const updateProduct = (variant, { updateHistory = true } = {}) => {
      selectedVariant = variant || null;
      selectedSize = variant?.size || '';

      sizeButtons.forEach((button) => {
        button.setAttribute('aria-checked', String(Boolean(variant) && String(button.dataset.variantId) === String(variant.id)));
      });

      if (!variant) {
        if (status) status.hidden = true;
        if (shippingNote) shippingNote.hidden = true;
        if (acceleratedCheckout) acceleratedCheckout.hidden = true;
        if (propertyInput) propertyInput.value = '';
        if (stickyVariant) stickyVariant.textContent = t.scegliTaglia;
        setButtonState(t.scegliTaglia, true);
        syncQuantity();
        return;
      }

      const state = inventoryState(variant);
      const inventory = Number(variant.inventory) || 0;
      const preorder = state === 'preorder';
      const lowStock = state === 'low';
      const buttonLabel = preorder ? t.preordina : t.aggiungi;
      const deliveryText = preorder ? t.consegnaIn : t.parteIn;
      const propertyValue = preorder ? t.propPreordine : t.propPronta;

      if (status) {
        status.hidden = false;
        status.classList.toggle('is-preorder', preorder);
        status.classList.toggle('is-low-stock', lowStock);
      }

      if (statusTitle) {
        if (preorder) statusTitle.textContent = t.suOrdinazione;
        else if (lowStock) statusTitle.textContent = inventory === 1 ? t.neRestanoUno : t.neRestano.replace('999', String(inventory));
        else statusTitle.textContent = t.inMagazzino;
      }

      if (statusDetail) statusDetail.textContent = deliveryText;
      if (shippingNote) {
        shippingNote.hidden = false;
        shippingNote.textContent = deliveryText;
      }
      if (acceleratedCheckout) acceleratedCheckout.hidden = false;
      if (variantInput) variantInput.value = String(variant.id);
      if (propertyInput) propertyInput.value = propertyValue;
      if (price) price.textContent = variant.price;
      if (stickyPrice) stickyPrice.textContent = variant.price;
      if (stickyVariant) stickyVariant.textContent = `Taglia ${variant.size}`;
      if (stickyImage && variant.image) stickyImage.src = variant.image;

      setButtonState(buttonLabel, false);
      syncQuantity();
      announceVariantInput();

      if (updateHistory) updateUrl(variant);
    };

    const variantForSelection = (size, color = selectedColor) =>
      variants.find((variant) => {
        const sizeMatches = variant.size === size;
        const colorMatches = !colorPosition || !color || variant.color === color;
        return sizeMatches && colorMatches;
      });

    sizeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const size = button.dataset.sizeValue || '';
        const directVariant = variants.find((variant) => String(variant.id) === String(button.dataset.variantId));
        const variant = colorPosition ? variantForSelection(size) : directVariant;
        updateProduct(variant || directVariant);
      });
    });

    colorButtons.forEach((button) => {
      button.addEventListener('click', () => {
        selectedColor = button.dataset.colorValue || '';
        colorButtons.forEach((candidate) => {
          candidate.setAttribute('aria-checked', String(candidate === button));
        });
        if (colorLabel) colorLabel.textContent = selectedColor;

        if (selectedSize) updateProduct(variantForSelection(selectedSize));
      });
    });

    quantityMinus?.addEventListener('click', () => {
      if (!quantityInput) return;
      quantityInput.value = String(Math.max(1, (Number.parseInt(quantityInput.value, 10) || 1) - 1));
      syncQuantity();
    });

    quantityPlus?.addEventListener('click', () => {
      if (!quantityInput || quantityPlus.disabled) return;
      const maximum = quantityMaximum(selectedVariant);
      quantityInput.value = String(Math.min(maximum, (Number.parseInt(quantityInput.value, 10) || 1) + 1));
      syncQuantity();
    });

    root.querySelector('[data-size-guide-link]')?.addEventListener('click', () => {
      const guide = root.querySelector(`#oneshot-size-guide-${CSS.escape(root.dataset.sectionId)}`);
      const summary = guide?.querySelector('summary');
      if (!guide || !summary) return;

      guide.open = true;
      summary.focus({ preventScroll: true });
      summary.scrollIntoView({
        block: 'center',
        behavior: window.matchMedia('(prefers-reduced-motion: no-preference)').matches ? 'smooth' : 'auto',
      });
    });

    stickyButton?.addEventListener('click', () => {
      if (stickyButton.disabled || !mainButton || !productForm) return;
      productForm.requestSubmit(mainButton);
    });

    if (mainButton && stickyBar) {
      let stickyFrame = 0;
      const syncStickyVisibility = () => {
        if (stickyFrame || !root.isConnected) return;
        stickyFrame = requestAnimationFrame(() => {
          stickyFrame = 0;
          const visible = mainButton.getBoundingClientRect().bottom <= 0;
          stickyBar.dataset.visible = String(visible);
          stickyBar.setAttribute('aria-hidden', String(!visible));
          stickyBar.toggleAttribute('inert', !visible);
        });
      };
      const stickyObserver = new IntersectionObserver(syncStickyVisibility, { threshold: 0 });
      stickyObserver.observe(mainButton);
      // Capturing also observes Horizon's desktop .page-wrapper scroll. A jump from
      // below to above the viewport can leave an IntersectionObserver non-intersecting
      // throughout, so scroll and resize must reconcile visibility too.
      document.addEventListener('scroll', syncStickyVisibility, { capture: true, passive: true });
      window.addEventListener('resize', syncStickyVisibility, { passive: true });
      document.addEventListener('shopify:section:unload', (event) => {
        if (!event.target.contains(root)) return;
        document.removeEventListener('scroll', syncStickyVisibility, true);
        window.removeEventListener('resize', syncStickyVisibility);
        stickyObserver.disconnect();
        buttonObserver.disconnect();
        cancelAnimationFrame(stickyFrame);
      });

      const buttonObserver = new MutationObserver(syncStickyFromMain);
      buttonObserver.observe(mainButton, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['disabled'],
      });
    }

    const requestedVariantId = new URL(window.location.href).searchParams.get('variant');
    const requestedVariant = variants.find((variant) => String(variant.id) === requestedVariantId);
    updateProduct(requestedVariant || null, { updateHistory: false });
    initGallery(root);
  };

  const initialize = (scope = document) => {
    if (scope.matches?.(SELECTORS.root)) initProduct(scope);
    scope.querySelectorAll?.(SELECTORS.root).forEach(initProduct);
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
